import { Injectable } from "@angular/core";
import { Data } from "@angular/router";
import { concat, interval, of, throwError } from "rxjs";
import { catchError, delay, map, take, takeUntil, timeout } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { JoinedSyndicateComponent } from "src/app/components/joined-syndicate/joined-syndicate.component";
import { RegProfileDialogComponentComponent } from "src/app/components/reg-profile-dialog-component/reg-profile-dialog-component.component";
import { SyndicateTicketsComponent } from "../../syndicate/syndicate-tickets/syndicate-tickets.component";
import { CheckoutComponent } from "../checkout/checkout.component";
import { StorePaymentComponent } from "../store-payment/store-payment.component";

/**
 * Facade that owns the product payment / syndicate-join interaction flow that
 * previously lived directly on `ProductComponent`. Each method receives the host
 * `ProductComponent` instance so behaviour and state mutations stay identical to
 * the pre-refactor implementation.
 */
@Injectable()
export class ProductPaymentFacadeService {
  // This function is used to join the syndicate
  joinSyndicate(host: any): void {
    host.storeService.userBackout$.next(false);
    if (host.promoName === "cash_syndicate_join") {
      host.callPromoCF("click");
    }
    const startTime = new Date();
    let endTime = new Date();
    const dwellTime = endTime.getTime() - startTime.getTime();
    const formattedDwellTime = host.AuthSyndicateService.convertMillisecondsToTime(dwellTime);

    const body = {
      Lottery_Product: host.productName,
      Payment_Method: host.offers?.payment_type,
      Draw_Days: host.drawDay$.value,
      Line_Count: host.offerRange$.value + 1,
      Total_Amount: host.initialPrice,
      Journey_ID: host.journeyId,
      For_how_many_weeks:
        host.offerDiscount$.value?.title === "No extension" ? "Only once" : host.offerDiscount$.value?.title,
    };
    host.mixPanelService.GTMPopupInitializationForInboxNew("Lottery Product Page Clicked button to join", body);
    try {
      if (host.irishProduct && host.offerRange$?.value == 4)
        //show even numbers for irish product
        host.offerRange$.next(host.offerRange$.value + 1);
      if (host.offerDiscount$?.value?.type === "oneoff") {
        //This method has been call Hot Jar store_one_off event
        host.GTMSagmentService.HotJarEvent("store_one_off");
        const checkoutDialog = host.bottomSheet.open(CheckoutComponent, {
          panelClass: "checkout-bottomsheet",
          data: {
            selectedDay: host.drawDay$?.value,
            selectedLine: host.offerRange$?.value,
            selectedDiscount: host.storeOffers?.cash_offer_discount[0] ?? host.offerDiscount$?.value,
            storeOffers: host.storeOffers,
            productOffers: host.productOffers$?.value,
            eventData: {
              ...body,
              Dwell_Time: formattedDwellTime,
            },
          },
        });

        checkoutDialog.afterDismissed()?.subscribe(
          (response) => {
            try {
              if (response)
                if (!host.storeOffers || !host.storeOffers?.cash_offer_discount[0])
                  /* Payment Dialog Bottom Sheet */
                  return;
              if (response && response?.checkout) {
                host.offerDiscount$.next(host.storeOffers?.cash_offer_discount[0]);
                let data = Object.assign(
                  {
                    line: response?.selectedLine,
                    real_name: host.productName,
                    prize: response.offers?.int_price?.[host.preferredCurrencySignal()]
                      ? response.offers?.int_price?.[host.preferredCurrencySignal()]
                      : response.offers?.price,
                    page: "store",
                  },
                  response
                );
                const For_how_many_week =
                  host.offerDiscount$.value?.title === "No extension" ? "Only once" : host.offerDiscount$.value?.title;
                const Syndicate_type = host.offerDiscount$.value?.type;
                const { For_how_many_weeks, ...bodyWithoutForHowManyWeeks } = body;
                let checkoutBody = {
                  ...bodyWithoutForHowManyWeeks,
                  Dwell_Time: formattedDwellTime,
                  Total_Amount: data?.offers?.price,
                  For_how_many_week_Syndicate_type: `${For_how_many_week}/${Syndicate_type}`,
                  Extension_Period: data?.selectedDiscount?.title,
                };
                host.mixPanelService.GTMPopupInitializationForInboxNew("Extend and Save Popup Clicked", checkoutBody);
                this.storePaymentPage(host, data);
              }
            } catch (e) { }
          },
          (err) => {
            return err;
          }
        );
      } else if (host.offerDiscount$.value.type === "subscription") {
        // This method has been used to call  store_purchase_ongoing HotJarEvent
        host.GTMSagmentService.HotJarEvent("store_purchase_ongoing");
        let data = Object.assign({
          line: host.offerRange$.value,
          real_name: host.productName,
          prize: host.offers?.int_price?.[host.preferredCurrencySignal()]
            ? host.offers?.int_price?.[host.preferredCurrencySignal()]
            : host.offers?.price,
          page: "store",
          selectedDay: host.drawDay$?.value,
          checkout: true,
          offers: host.offers,
          selectedDiscount: host.offerDiscount$?.value,
          selectedLine: host.offerRange$?.value,
        });
        this.storePaymentPage(host, data);
      }

      let eventName;
      let isUpdatedCelebration = false;
      if (host.offers?.ongoing_flag) {
        eventName = "join_managed_ongoing_cash_syndicate";
      } else {
        eventName = "join_managed_one-off_cash_syndicate";
      }

      host.trackedEventsService.listOfEvents$?.subscribe(
        (d) => {
          d.some((f) => f?.id === eventName) ? host.trackedEventsService.updateTrackedUserEvent(eventName) : null;
        },
        (err) => {
          return err;
        }
      );

      host.trackedEventsService.listOfEvents$?.subscribe((d) => {
        d?.map(
          (data: any) => {
            if (data.id === eventName && !isUpdatedCelebration) {
              host.trackedEventsService.updateCelebration(data);
              isUpdatedCelebration = true;
            }
          },
          (err) => {
            return err;
          }
        );
      });
    } catch (e) { }
  }

  // This function is used to add new card
  addNewCard(host: any, data: any): void {
    host.paymentService
      .insertCustomerPurchase(data, "/api_post_request/insert_customer_purchase_store_product")
      ?.subscribe(
        (result: any) => {
          if (!result || !result?.response) return;

          if (result?.status === "rejected") {
            host.authService
              .getConstantsMsg(result?.error_code)
              .then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong!",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                host.authService.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((err) => {
                host.authService.errorHandlerLogs("Error fetching constants message:", err);
                return err;
              });
            return;
          }

          if (result && result?.response && result?.response?.status === "SUCCESS") {
            const data1 = { ...data, ...{ customer_purchase: result?.response } };
            const myuuid = uuidv4();
            host.paymentService.updateSyndicateRequest(data1, myuuid);

            host.paymentService.preferredCurrencyObj = {
              amount: String(data?.offers?.int_price?.[host.preferredCurrencySignal()]?.toFixed(2)),
              currency: host.preferredCurrencySignal(),
            };

            const paymentReason = "offer";
            host.paymentService
              .getCheckoutId(
                parseFloat(data?.prize)?.toFixed(2),
                host.preferredCurrencySignal() || data?.offers?.currency,
                {},
                paymentReason
              )
              ?.subscribe(
                async (res) => {
                  host.paymentService.preferredCurrencyObj = null;

                  // Add null-safety check for res BEFORE accessing properties
                  if (!res) {
                    host.authService.errorHandlerLogs("getCheckoutId returned null response - user authentication may be invalid");
                    host.authService.getConstantsMsg("1088").then((translatedMsg) => {
                      const defaultMsg = {
                        message: {
                          line_1: "Unable to process payment. Please try logging in again.",
                          line_2: "",
                        },
                        style: {
                          type: "Error",
                          icon: "tick circle error.svg",
                        },
                      };
                      host.authService.showToastMessage(translatedMsg, defaultMsg);
                    }).catch((err) => {
                      host.authService.errorHandlerLogs("Error fetching constants message:", err);
                      return err;
                    });
                    return;
                  }

                  if (res.id) {
                    const userId = host.userId;
                    if (!userId) return;

                    const path_id = "At4Vyy58QQ2vknYAhw7T";
                    const doc_data = {
                      checkout_response: {
                        ...res,
                        stared: new Date()
                      }
                    };
                    const dv = { docId: String(myuuid) };
                    const method = "update";

                    host.authService.getV2CloudFunction("updatefirestoredoc", {
                      path_id,
                      doc_data,
                      dv,
                      method,
                    }).subscribe({
                      error: (err) => {
                        host.authService.errorHandlerLogs('Error in addNewCard:', err);
                      }
                    });

                    host.router.navigate([`/store/addnew/${String(myuuid)}`]);
                  } else {
                    host.authService.getConstantsMsg("1014").then((translatedMsg) => {
                      const defaultMsg = {
                        message: {
                          line_1: "something went wrong",
                          line_2: "",
                        },
                        style: {
                          type: "Error",
                          icon: "tick circle error.svg",
                        },
                      };
                      host.authService.showToastMessage(translatedMsg, defaultMsg);
                    }).catch((err) => {
                      host.authService.errorHandlerLogs("Error fetching constants message:", err);
                      return err;
                    });
                  }
                },
                (err) => {
                  host.authService.errorHandlerLogs("Error in checkout ID subscription:", err);
                  return err;
                }
              );
          }
        },
        (err) => {
          host.authService.errorHandlerLogs("Error in insert customer purchase subscription:", err);
        }
      );
  }

  // This function is used to open payment modal
  async storePaymentPage(host: any, data: any): Promise<void> {
    /*  if (!host.tokenExists) {
       this.addNewCard(host, data);
       return;
     } */
    const isOfferCappingBlocked = await host.storeService.validateOfferCappingBeforePurchase(
      data,
      "store_product_modal"
    );
    if (isOfferCappingBlocked) {
      return;
    }

    localStorage.setItem("payType", "cash");
    host.modalController
      .create({
        component: StorePaymentComponent,
        cssClass: "modal-fullScreen",
        componentProps: {
          data: data,
        },
      })
      .then((paymentModal) => {
        paymentModal.present();
        paymentModal.onDidDismiss().then((res: any) => {
          try {
            if (res?.data && res?.data?.p_type == "10") {
              host.sqlAPICallResponse = res?.data?.syndicateId;
              if (!host.sqlAPICallResponse) {
                host.voucherService.setSyndicatePending();
                let payType = "";
                if (localStorage.getItem("payType")) {
                  payType = localStorage.getItem("payType");
                  localStorage.removeItem("payType");
                }
                if (payType === "cash") {
                  host.router.navigateByUrl("mainTabs/tabs/syndicate");
                } else if (payType === "cash-issue") {
                  host.loadingService.dismiss();
                  host.router.navigateByUrl("mainTabs/tabs/store");
                } else {
                  const decline = host.matDailog.open(RegProfileDialogComponentComponent, {
                    panelClass: "ongoingWarning-size",
                    backdropClass: "ongoingWarning-back-drop-color",
                    disableClose: true,
                  });
                }
                //this.location.back();
                return;
              }
            }
            if (res?.data && res?.data.syndicateId) {
              host.paymentService
                .getSyndicateInfoWithProducts(res?.data.syndicateId)
                .pipe(takeUntil(host.destroy$),
                  map((detail: any) => {
                    if (!detail[0]) return;
                    const syndicateInfo = detail[0];
                    if (!syndicateInfo) return;
                    syndicateInfo.product = [];
                    const productDocs = detail && detail[1] && detail[1].docs ? detail[1].docs : [];
                    for (let index = 0; index < productDocs?.length; index++) {
                      if (productDocs[index] && typeof productDocs[index].data === 'function') {
                        syndicateInfo?.product?.push(productDocs[index].data());
                      }
                    }

                    syndicateInfo["cardType"] = "Managed";
                    if (host.promoName === "cash_syndicate_join") {
                      host.callPromoCF('conversion');
                    }
                    return syndicateInfo;
                  })
                )
                ?.subscribe(
                  (syndicateInfo) => {
                    this.showTicket(host, syndicateInfo);
                  },
                  (err) => {
                    throw err;
                  }
                );
            }
          } catch (e) { }
        });
      })
      .catch((err) => {
        return err;
      });
  }

  // This function initiates the animation of the spinner
  startSpinner(host: any): void {
    host.spinnerAnimation = true;
    host.progressBar = 0;

    const twentyFive = 25;
    const fifty = 50;
    const ninety = 90;
    const oneTwenty = 120;

    const timer50$ = interval(fifty).pipe(take(fifty));
    const timer90$ = interval(ninety).pipe(take(twentyFive));
    const timer120$ = interval(oneTwenty).pipe(take(twentyFive));

    host.sub?.unsubscribe();

    concat(timer50$, timer90$, timer120$)
      .pipe(delay(0))
      .subscribe({
        next: () => {
          host.progressBar += 1;
        },
        error: (err) => {
          return err;
        },
      });
  }

  // This function is used to process credit payment
  makeCreditPayment(host: any): void {
    if (!host.isNavigationAllowed) {
      return;
    }
    host.isNavigationAllowed = false;
    this.startSpinner(host);
    let eventName;
    let isUpdatedCelebration;
    // const id = this.paymentService.createSyndicateRequest(this.offers);
    //NEW CALL START
    if (host.promoName === "credit_syndicate_join" || host.promoName === "promo_credit_offer") {
      host.callPromoCF('click');
    }

    host.offers.product_name = host.productName.toLowerCase() || "";
    const selectedOffersCredit = JSON.parse(JSON.stringify(host.offers || {}));
    // User selects the credit payment method
    host.storeService.userOfferCapping$.subscribe((data: Data) => {
      if (!data) {
        host.payNow.set(true);
        return;
      }
      // Filter the data based on the selected draw day and product name
      const offerCappingData = data ?? [];
      const filteredData = offerCappingData?.filter((res: Data) => {
        const drawDate = new Date(res?.draw_date?.seconds * 1000) >= new Date() ? true : false; // Convert seconds to milliseconds
        const drawDays = selectedOffersCredit?.draw_day?.find((d) => d?.toLowerCase() === res?.draw_day?.toLowerCase()) || false;
        const isProduct = res?.product_name?.toLowerCase() === selectedOffersCredit?.product_name?.toLowerCase();
        return drawDate && drawDays && isProduct;
      });
      // Check if filteredData is empty or if product name is not in the filtered data
      const isProductAvailableInData = offerCappingData?.some(
        (res) => res?.product_name?.toLowerCase() === selectedOffersCredit?.product_name?.toLowerCase()
      );
      // Check if filteredData is empty or if product name is not in the filtered data
      if (!isProductAvailableInData || filteredData.length > 0 || !data) host.payNow.set(true);

    });

    // Checked the Offer is available or not
    if (host.payNow()) {
      host.paymentService?.checkCreditBalance()?.subscribe((res: Data) => {
        let balanceDetails: Data = res && typeof res.data === "function" ? res.data() : null;
        if (balanceDetails && balanceDetails.credit && balanceDetails.credit >= host.offers?.price) {
          host.paymentService?.doCreditPayment(host.offers)?.pipe(
            timeout(3000), // ⏳ 3 sec wait
            catchError((err) => {
              if (err?.name === 'TimeoutError') {
                host.isNavigationAllowed = true;
                host.authService.getConstantsMsg("1163").then(translatedMsg => {
                  host.authService.showToastMessage(translatedMsg, {
                    message: {
                      line_1:
                        "Your tickets are being generated. You’ll see them shortly in your syndicate tab."
                    },
                    style: {
                      icon: "tick circle success.png",
                      type: "Success"
                    }
                  });
                });
                host.router.navigate(['/store']);
                return of(null); // stream break
              }
              return throwError(() => err);
            })
          )?.subscribe({
            next: (result: Data) => {
            if (!result || !result?.response) {
              host.isNavigationAllowed = true;
              return;
            }
            host.progressBar = 100;
            host.sub?.unsubscribe();
            const responsObj = { ...selectedOffersCredit, ...result?.response, productName: host.productName };
            // this.paymentService.updateSyndicateRequest(responsObj, id);
            if (result?.status === "rejected") {
              host.isNavigationAllowed = true;
              host.authService.getConstantsMsg(result?.error_code).then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Please wait a few minutes between each request.",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                host.authService.showToastMessage(translatedMsg, defaultMsg);
              });
              return;
            }
            if (result && result?.response && result?.response?.status === "SUCCESS") {
              if (host.promoName === "credit_syndicate_join" || host.promoName === "promo_credit_offer") {
                host.callPromoCF('conversion');
              }
              setTimeout(() => {
                host.isNavigationAllowed = true;
                this.openSuccessInfoPopup(host, responsObj);
              }, 3000);
            } else if (result && result?.response && result?.response?.status === "FAIL") {
              host.isNavigationAllowed = true;
              const toasterMsg = result?.response?.message;
              const defaultMsg = {
                message: {
                  line_1: toasterMsg,
                  line_2: "",
                },
                style: {
                  type: "Error",
                  icon: "tick circle error.svg",
                },
              };
              host.authService.showToastMessage(toasterMsg, defaultMsg);
            }
            else {
              host.isNavigationAllowed = true;
              host.authService.getConstantsMsg("1104").then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Please wait a few minutes between each request.",
                    line_2: "",
                  },
                  style: {
                    type: "Error",
                    icon: "tick circle error.svg",
                  },
                };
                host.authService.showToastMessage(translatedMsg, defaultMsg);
              });
            }
            setTimeout(() => {
              host.spinnerAnimation = false;
              host.proceed = false;
              setTimeout(() => {
                host.proceed = true;
              }, 2000);
            }, 300);
            },
            error: (err) => {
              host.isNavigationAllowed = true;
              // this.authService.getConstantsMsg("1104").then((translatedMsg) => {
              const translatedMsg = "";
              const defaultMsg = {
                message: {
                  line_1: "Some error has occured.",
                  line_2: err,
                },
                style: {
                  type: "Error",
                  icon: "tick circle error.svg",
                },
              };
              host.authService.showToastMessage(translatedMsg, defaultMsg);
              // });
            },
          });
        } else {
          host.isNavigationAllowed = true;
          host.authService
            .getConstantsMsg("1055")
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "You do not have enough credit to complete this transaction",
                  line_2: "",
                },
                style: {
                  type: "Error",
                  icon: "tick circle error.svg",
                },
              };
              host.authService.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((err) => {
              return err;
            });
        }
      });

      //NEW CALL END

      eventName = "join_managed_one-off_credit_syndicate";

      host.trackedEventsService.listOfEvents$?.subscribe(
        (d) => {
          d.some((f) => f.id === eventName) ? host.trackedEventsService.updateTrackedUserEvent(eventName) : null;
        },
        (err) => {
          return err;
        }
      );

      host.trackedEventsService?.listOfEvents$?.subscribe(
        (d) => {
          d?.map((data: any) => {
            if (data?.id === eventName && !isUpdatedCelebration) {
              host.trackedEventsService.updateCelebration(data);
              isUpdatedCelebration = true;
            }
          });
        },
        (err) => {
          return err;
        }
      );
    } else {
      host.runSpinner.set(true);
      host.isNavigationAllowed = true;
      host.authService?.getConstantsMsg("1151").then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: `You’ve reached your purchase limit for this upcoming draw.`,
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        host.authService?.showToastMessage(translatedMsg, defaultMsg);
      });
    }

  }

  // This function is used to open success popup
  openSuccessInfoPopup(host: any, syndicateObj): void {
    host.syndicateObjForTicket = syndicateObj;
    const confirmationDialog = host.dialog.open(JoinedSyndicateComponent, {
      panelClass: "joined-syndicate-dialog",
      backdropClass: "cashout-back-drop-color",
      data: syndicateObj,
    });

    if (!syndicateObj?.encrypt_syndicate_id) {
      host.storeService.getSyndicateRequestId(syndicateObj?.pwa_syndicate_request_id)?.subscribe((resData: Data) => {
        syndicateObj.encrypt_syndicate_id = resData?.encrypt_syndicate_id;
      });
    }

    confirmationDialog.afterClosed()?.subscribe(
      async (dialogResponse: boolean) => {
        host.isNavigationAllowed = true;
        if (!dialogResponse) return;
        if (dialogResponse && syndicateObj?.encrypt_syndicate_id) {
          try {
            if (!syndicateObj?.encrypt_syndicate_id) {
              host.router.navigateByUrl("mainTabs/tabs/syndicate");
            } else {
              host.paymentService
                .getSyndicateInfoWithProducts(syndicateObj?.encrypt_syndicate_id)
                .pipe(takeUntil(host.destroy$),
                  map((detail: any) => {
                    const syndicateInfo = detail[0];
                    if (!syndicateInfo) return;
                    syndicateInfo["product"] = [];
                    const productDocs = detail && detail[1] && detail[1].docs ? detail[1].docs : [];
                    for (let index = 0; index < productDocs.length; index++) {
                      if (productDocs[index] && typeof productDocs[index].data === 'function') {
                        syndicateInfo?.product?.push(productDocs[index].data());
                      }
                    }
                    if (!syndicateInfo) return;
                    syndicateInfo["cardType"] = "Managed";
                    return syndicateInfo;
                  })
                )
                ?.subscribe(
                  (syndicateInfo) => {
                    if (!syndicateInfo) return;
                    this.showTicket(host, syndicateInfo);
                  },
                  (err) => {
                    throw err;
                  }
                );
            }
          } catch (e) {
            return e;
          }
        } else {
          this.showTicket(host, "");
        }

        //this.trackedEventsService.openAnimation();
      },
      (err) => {
        return err;
      }
    );
  }

  // This function is used to open ticket popup
  async showTicket(host: any, syndicateInfo): Promise<any> {
    const syndicateTickets = await host.modalController.create({
      component: SyndicateTicketsComponent,
      componentProps: {
        syndicate: syndicateInfo,
        syndicateObj: host.syndicateObjForTicket,
      },
      cssClass: "modal-fullScreen",
      // enterAnimation: CustomAnimation.enterFromRight,
      //  leaveAnimation: CustomAnimation.exitThroughRight,
    });
    return await syndicateTickets.present();
  }
}
